import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Định nghĩa các loại theme
export type ThemeType = 'dark' | 'light';

// Định nghĩa các màu theo theme
interface ThemeColors {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  cardBackground: string;
  textColor: string;
}

// Theme mặc định - dark mode
const darkTheme: ThemeColors = {
  primaryColor: '#20757A',     // Xanh Vissoft
  accentColor: '#FC5200',      // Cam Strava
  backgroundColor: '#121212',  // Nền tối (đen)
  cardBackground: '#1E1E1E',   // Nền tối hơn (đen nhạt)
  textColor: '#FFFFFF',        // Trắng
};

// Theme light mode
const lightTheme: ThemeColors = {
  primaryColor: '#20757A',     // Xanh Vissoft (giữ nguyên)
  accentColor: '#FC5200',      // Cam Strava (giữ nguyên)
  backgroundColor: '#F5F5F5',  // Nền sáng
  cardBackground: '#FFFFFF',   // Trắng
  textColor: '#333333',        // Đen
};

// Định nghĩa kiểu dữ liệu cho context
interface ThemeContextType {
  theme: ThemeType;
  themeColors: ThemeColors;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

// Tạo context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Hook để sử dụng theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Props cho ThemeProvider
interface ThemeProviderProps {
  children: ReactNode;
}

// Component ThemeProvider
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Lấy theme từ localStorage hoặc sử dụng theme mặc định
  const [theme, setThemeState] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as ThemeType) || 'light';
  });

  // Lưu theme vào localStorage khi thay đổi
  useEffect(() => {
    localStorage.setItem('theme', theme);
    applyThemeToDocument(theme);
  }, [theme]);

  // Hàm áp dụng theme vào document
  const applyThemeToDocument = (selectedTheme: ThemeType) => {
    document.documentElement.setAttribute('data-theme', selectedTheme);
    
    // Tùy chỉnh các biến CSS dựa trên theme
    if (selectedTheme === 'light') {
      // Màu nền cho light mode
      document.documentElement.style.setProperty('--background-main', '#FFFFFF');
      document.documentElement.style.setProperty('--background-dark', '#F5F5F5');
      document.documentElement.style.setProperty('--background-darker', '#EEEEEE');
      document.documentElement.style.setProperty('--background-card', '#FFFFFF');
      document.documentElement.style.setProperty('--background-input', '#F5F5F5');
      document.documentElement.style.setProperty('--background-hover', '#F0F0F0');
      
      // Đảm bảo tất cả chữ có màu đen khi ở chế độ sáng
      document.documentElement.style.setProperty('--text-primary', '#000000');
      document.documentElement.style.setProperty('--text-secondary', '#333333');
      document.documentElement.style.setProperty('--text-muted', '#555555');
      document.documentElement.style.setProperty('--border-color', '#DDDDDD');
      
      // Màu bổ sung cho các thành phần cụ thể
      document.documentElement.style.setProperty('--table-header-bg', '#F5F5F5');
      document.documentElement.style.setProperty('--table-border', '#EEEEEE');
      document.documentElement.style.setProperty('--card-shadow', '0 2px 8px rgba(0, 0, 0, 0.08)');
      document.documentElement.style.setProperty('--input-text', '#333333');
      document.documentElement.style.setProperty('--dashboard-bg', '#F5F5F5');
      document.documentElement.style.setProperty('--sidebar-bg', '#FFFFFF');
      document.documentElement.style.setProperty('--sidebar-active', '#F5F5F5');
      document.documentElement.style.setProperty('--header-border', '#DDDDDD');
      
      // Đảm bảo màu text của các thành phần khác cũng được cập nhật
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    } else {
      // Khôi phục về dark theme (mặc định)
      document.documentElement.style.setProperty('--background-main', '#121212');
      document.documentElement.style.setProperty('--background-dark', '#1E1E1E');
      document.documentElement.style.setProperty('--background-darker', '#2D2D2D');
      document.documentElement.style.setProperty('--background-card', '#1E1E1E');
      document.documentElement.style.setProperty('--background-input', '#2D2D2D');
      document.documentElement.style.setProperty('--background-hover', '#2D2D2D');
      
      // Đảm bảo tất cả chữ có màu trắng khi ở chế độ tối
      document.documentElement.style.setProperty('--text-primary', '#FFFFFF');
      document.documentElement.style.setProperty('--text-secondary', '#FFFFFF');
      document.documentElement.style.setProperty('--text-muted', '#DDDDDD');
      document.documentElement.style.setProperty('--border-color', '#FC5200');
      
      // Màu bổ sung cho các thành phần cụ thể
      document.documentElement.style.setProperty('--table-header-bg', '#1E1E1E');
      document.documentElement.style.setProperty('--table-border', '#FC5200');
      document.documentElement.style.setProperty('--card-shadow', '0 2px 8px rgba(0, 0, 0, 0.2)');
      document.documentElement.style.setProperty('--input-text', '#FFFFFF');
      document.documentElement.style.setProperty('--dashboard-bg', '#121212');
      document.documentElement.style.setProperty('--sidebar-bg', '#1E1E1E');
      document.documentElement.style.setProperty('--sidebar-active', '#2D2D2D');
      document.documentElement.style.setProperty('--header-border', '#FC5200');
      
      // Đảm bảo màu text của các thành phần khác cũng được cập nhật
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    }
    
    // Gửi sự kiện thông báo rằng theme đã thay đổi cho các component khác lắng nghe
    const event = new CustomEvent('themeChange', { detail: { theme: selectedTheme } });
    document.dispatchEvent(event);
  };

  // Lấy màu sắc theo theme hiện tại
  const themeColors = theme === 'light' ? lightTheme : darkTheme;

  // Hàm chuyển đổi theme
  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  // Hàm thiết lập theme cụ thể
  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeColors, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 